"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useUser } from "@clerk/nextjs";
import { toast } from "sonner";
import type { Comment, CommentFormData } from "@/lib/types";
import CommentForm from "@/components/CommentForm";
import CommentItem from "@/components/CommentItem";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { useCommentDeepLink } from "@/hooks/useCommentDeepLink";
import {
  getCommentsLikeDataAction,
  toggleCommentLikeAction,
} from "@/lib/actions/commentLikes";
import {
  createComment,
  editComment,
  getComments,
  removeComment,
} from "@/lib/actions/comments";

// 모든 댓글 ID 재귀 추출 (컴포넌트 외부 - immutability 규칙 회피)
function getAllCommentIds(list: Comment[]): string[] {
  return list.flatMap((c) => [
    c.id,
    ...(c.replies ? getAllCommentIds(c.replies) : []),
  ]);
}

// 댓글에 좋아요 수 주입 (컴포넌트 외부)
function injectLikeCounts(
  list: Comment[],
  likeCounts: Record<string, number>
): Comment[] {
  return list.map((c) => ({
    ...c,
    likeCount: likeCounts[c.id] ?? 0,
    replies: c.replies ? injectLikeCounts(c.replies, likeCounts) : [],
  }));
}

function addReplyToComment(list: Comment[], parentId: string, newReply: Comment): Comment[] {
  return list.map((c) => {
    if (c.id === parentId) {
      return { ...c, replies: [...(c.replies || []), newReply] };
    }
    if (c.replies && c.replies.length > 0) {
      return { ...c, replies: addReplyToComment(c.replies, parentId, newReply) };
    }
    return c;
  });
}

function replaceReplyInComment(list: Comment[], tempId: string, newReply: Comment): Comment[] {
  return list.map((c) => {
    if (c.id === tempId) return { ...newReply, replies: c.replies || [] };
    if (c.replies && c.replies.length > 0) {
      return { ...c, replies: replaceReplyInComment(c.replies, tempId, newReply) };
    }
    return c;
  });
}

function removeCommentById(list: Comment[], id: string): Comment[] {
  return list
    .filter((c) => c.id !== id)
    .map((c) => ({
      ...c,
      replies: c.replies ? removeCommentById(c.replies, id) : [],
    }));
}

function updateCommentInComments(
  list: Comment[],
  commentId: string,
  updateFn: (c: Comment) => Comment
): Comment[] {
  return list.map((c) => {
    if (c.id === commentId) return updateFn(c);
    if (c.replies && c.replies.length > 0) {
      return { ...c, replies: updateCommentInComments(c.replies, commentId, updateFn) };
    }
    return c;
  });
}

/** 댓글 영역 앵커 id — 모바일 dock "댓글" 버튼(`CommunityBoardMobileDock`)이 여기로 스크롤한다. */
export const THREAD_COMMENTS_ANCHOR_ID = "comments";

interface ThreadCommentSectionProps {
  /** 댓글 slug — 이슈 게시판 `community/issues/<id>`, 잡담 게시판 `community/talk/<id>` */
  slug: string;
  /** 댓글이 없을 때 보여줄 문구 */
  emptyText?: string;
  /**
   * true 면 비로그인 사용자는 읽기만 가능: 작성 폼 대신 로그인 안내를 보이고 답글 버튼을 숨긴다.
   * 최종 게이트는 `createComment` 서버 액션이 아니라 이 UI 이므로, 서버 강제가 필요하면 별도 액션을 둘 것.
   */
  requireSignIn?: boolean;
}

/**
 * 스레드형 게시판 공용 댓글 섹션 (JCG-3029 이슈 게시판·감자단 잡담).
 * - 기존 댓글 시스템(`getComments`/`createComment` 등)을 게시판별 slug 로 재사용.
 * - **오래된 순**(위→아래)으로 보여 대화 흐름을 따라 읽게 한다.
 *   (`getCommentsBySlug` 는 루트 댓글을 최신순으로 돌려주므로 여기서 뒤집는다.)
 * - 카테고리 탭 없음. 이슈 게시판 댓글은 분류값 없이 저장하고 별도 Linear 이슈를 만들지 않는다.
 */
export default function ThreadCommentSection({
  slug,
  emptyText = "아직 댓글이 없어요. 첫 댓글을 남겨보세요.",
  requireSignIn = false,
}: ThreadCommentSectionProps) {
  const { isLoaded, isSignedIn } = useUser();
  // 관리자는 남의 댓글·답글도 삭제할 수 있다(모더레이션). 섹션에서 한 번만 조회해 각 댓글에 내려보낸다.
  const { isAdmin } = useAdminStatus();
  const pathname = usePathname() ?? "/";
  // 로그인 전용 게시판에서 비로그인이면 작성·답글을 막는다(로드 전에는 막아 두었다가 확인되면 연다).
  const readOnly = requireSignIn && !(isLoaded && isSignedIn);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [likedCommentIds, setLikedCommentIds] = useState<Set<string>>(new Set());

  // 실패해도 목록을 비우지 않는다 — 첫 로드는 초기값이 이미 [] 이고, 재조회(알림 딥링크)는 보이던 목록을 유지해야 한다.
  const loadComments = useCallback(async () => {
    try {
      const result = await getComments(slug);
      if (!result.success) {
        console.error("댓글 로드 실패:", result.error);
        return;
      }
      const ordered = [...result.comments].reverse();
      const allIds = getAllCommentIds(ordered);
      if (allIds.length === 0) {
        setComments(ordered);
        return;
      }
      const likeData = await getCommentsLikeDataAction(allIds);
      if (likeData.success) {
        setComments(injectLikeCounts(ordered, likeData.likeCounts));
        setLikedCommentIds(new Set(likeData.likedIds));
      } else {
        setComments(ordered);
      }
    } catch (error) {
      console.error("댓글 로드 실패:", error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // 댓글 딥링크(#comment-{id}, JCG-3603) — 알림 벨(내 글 댓글·답글, JCG-3615)이 이 링크로 들어온다.
  // 탭·더보기가 없으므로 스크롤·강조·본문 펼침만 맡기고, 같은 페이지를 보는 동안 달린 새 댓글이면 훅이 목록을 다시 읽게 한다.
  const { targetCommentId: deepLinkTargetId, highlightedCommentId } = useCommentDeepLink({
    comments,
    loading,
    onMissingTarget: loadComments,
  });

  useEffect(() => {
    // 외부 데이터 fetch(getComments + getCommentsLikeData) 구독 패턴. RestorationCommentSection 과 동일.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadComments();
  }, [loadComments]);

  const handleAddComment = async (data: CommentFormData) => {
    const tempId = `temp_${Date.now()}`;
    const tempComment: Comment = {
      id: tempId,
      content: data.content,
      author: data.author,
      email: data.email,
      createdAt: new Date().toISOString(),
      replies: [],
    };
    setComments((prev) => [...prev, tempComment]);

    try {
      const result = await createComment(slug, data);
      if (result.success && result.comment) {
        setComments((prev) =>
          prev.map((c) => (c.id === tempId ? { ...result.comment!, replies: [] } : c))
        );
        toast.success("댓글을 남겼어요.");
        return true;
      }
      setComments((prev) => removeCommentById(prev, tempId));
      toast.error(result.error || "댓글 등록에 실패했습니다.");
      return false;
    } catch (error) {
      setComments((prev) => removeCommentById(prev, tempId));
      console.error("댓글 추가 실패:", error);
      toast.error("댓글 등록에 실패했습니다.");
      return false;
    }
  };

  const handleAddReply = async (parentId: string, data: CommentFormData) => {
    const tempId = `temp_${Date.now()}`;
    const tempReply: Comment = {
      id: tempId,
      content: data.content,
      author: data.author,
      email: data.email,
      createdAt: new Date().toISOString(),
      parentId,
      replies: [],
    };
    setComments((prev) => addReplyToComment(prev, parentId, tempReply));

    try {
      const result = await createComment(slug, data, parentId);
      if (result.success && result.comment) {
        setComments((prev) => replaceReplyInComment(prev, tempId, result.comment!));
        toast.success("답글을 남겼어요.");
        return true;
      }
      setComments((prev) => removeCommentById(prev, tempId));
      toast.error(result.error || "답글 등록에 실패했습니다.");
      return false;
    } catch (error) {
      setComments((prev) => removeCommentById(prev, tempId));
      console.error("답글 추가 실패:", error);
      toast.error("답글 등록에 실패했습니다.");
      return false;
    }
  };

  const handleEditComment = async (
    commentId: string,
    email: string,
    data: CommentFormData
  ) => {
    const original = [...comments];
    setComments((prev) =>
      updateCommentInComments(prev, commentId, (c) => ({
        ...c,
        content: data.content,
        author: data.author,
      }))
    );

    try {
      const result = await editComment(commentId, email, data, slug);
      if (result.success && result.comment) {
        setComments((prev) =>
          updateCommentInComments(prev, commentId, (c) => ({
            ...result.comment!,
            replies: c.replies || [],
            likeCount: c.likeCount,
          }))
        );
        toast.success("댓글을 수정했어요.");
        return true;
      }
      setComments(original);
      toast.error(result.error || "댓글 수정에 실패했습니다.");
      return false;
    } catch (error) {
      setComments(original);
      console.error("댓글 수정 실패:", error);
      toast.error("댓글 수정에 실패했습니다.");
      return false;
    }
  };

  const handleDeleteComment = async (commentId: string, email: string) => {
    const original = [...comments];
    setComments((prev) =>
      updateCommentInComments(prev, commentId, (c) => ({
        ...c,
        author: null,
        content: null,
        isDeleted: true,
      }))
    );
    const result = await removeComment(commentId, email, slug);
    if (result.success) {
      toast.success("댓글을 삭제했어요.");
    } else {
      setComments(original);
      toast.error(result.error || "댓글 삭제에 실패했습니다.");
    }
  };

  const handleLikeComment = async (commentId: string) => {
    const wasLiked = likedCommentIds.has(commentId);
    setLikedCommentIds((prev) => {
      const next = new Set(prev);
      if (wasLiked) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
    setComments((prev) =>
      updateCommentInComments(prev, commentId, (c) => ({
        ...c,
        likeCount: (c.likeCount ?? 0) + (wasLiked ? -1 : 1),
      }))
    );

    const result = await toggleCommentLikeAction(commentId);
    if (result.success) {
      setLikedCommentIds((prev) => {
        const next = new Set(prev);
        if (result.liked) next.add(commentId);
        else next.delete(commentId);
        return next;
      });
      setComments((prev) =>
        updateCommentInComments(prev, commentId, (c) => ({
          ...c,
          likeCount: result.likeCount,
        }))
      );
    } else {
      // 롤백
      setLikedCommentIds((prev) => {
        const next = new Set(prev);
        if (wasLiked) next.add(commentId);
        else next.delete(commentId);
        return next;
      });
      setComments((prev) =>
        updateCommentInComments(prev, commentId, (c) => ({
          ...c,
          likeCount: (c.likeCount ?? 0) + (wasLiked ? 1 : -1),
        }))
      );
    }
  };

  if (loading) {
    return (
      <div
        id={THREAD_COMMENTS_ANCHOR_ID}
        className="mt-4 rounded-md border-[0.5px] p-4 dark:border-neutral-500"
      >
        <div className="flex items-center justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-[#706E6E] dark:border-neutral-400" />
        </div>
      </div>
    );
  }

  return (
    <section
      id={THREAD_COMMENTS_ANCHOR_ID}
      aria-label="댓글"
      className="mt-4 rounded-md border-[0.5px] p-3 dark:border-neutral-500 sm:p-4"
    >
      <h2 className="mb-2 border-b-[0.5px] border-gray-200 pb-2 text-base font-semibold text-[#333] dark:border-neutral-400 dark:text-neutral-200 sm:text-lg">
        댓글 ({comments.length})
      </h2>

      <div className="space-y-0">
        {comments.length > 0 ? (
          comments.map((comment, index) => (
            <div key={comment.id}>
              <CommentItem
                comment={comment}
                onReply={handleAddReply}
                onEdit={handleEditComment}
                onDelete={handleDeleteComment}
                canModerate={isAdmin}
                onLike={handleLikeComment}
                likedCommentIds={likedCommentIds}
                replyDisabled={readOnly}
                deepLinkTargetId={deepLinkTargetId}
                highlightedCommentId={highlightedCommentId}
              />
              {index < comments.length - 1 && (
                <hr className="border-gray-200 dark:border-neutral-600" />
              )}
            </div>
          ))
        ) : (
          <p className="py-6 text-center text-sm font-light text-[#706E6E] dark:text-gray-400">
            {emptyText}
          </p>
        )}
      </div>

      {readOnly ? (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 px-4 py-4 text-center dark:border-amber-900/60 dark:bg-amber-950/20">
          <p className="text-sm text-amber-900 dark:text-amber-200">
            댓글과 답글은 로그인한 감자단만 남길 수 있어요. 읽는 건 누구나 가능해요.
          </p>
          {isLoaded && (
            <Link
              href={`/sign-in?redirect_url=${encodeURIComponent(pathname)}`}
              className="mt-3 inline-flex items-center justify-center rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 dark:bg-amber-400 dark:text-amber-950 dark:hover:bg-amber-300"
            >
              로그인하고 댓글 남기기
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-3 rounded-md border-[0.5px] border-gray-200 bg-[#FAFAFA] p-3 dark:border-neutral-400 dark:bg-gray-800">
          <CommentForm onSubmit={handleAddComment} />
        </div>
      )}
    </section>
  );
}
